import { Subscription } from "rxjs/Subscription";
import { AfterViewInit, Component, OnInit } from "@angular/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { Subject } from "rxjs/Subject";
import { ActivatedRoute, Router } from "@angular/router";
import { debounceTime } from "rxjs/operator/debounceTime";
import { TypeSetup as BillTypeSetup } from "../../shared/bill/type-setup";
import { Plan, Plan as BillTypePlan } from "../../shared/bill/type-setup/plan";
import { Constants } from "./../../helper/constants";
import { BillTypeService } from "./../../shared/bill/bill-type.service";
import { PriceDetail } from "./../../shared/bill/type-setup/price-detail";
import { LoaderService } from "../../shared/services/loader.service";

@Component({
  selector: "app-set-up-bill-type",
  templateUrl: "./set-up-bill-type.component.html",
  styleUrls: ["./set-up-bill-type.component.css"]
})
export class SetUpBillTypeComponent implements AfterViewInit, OnInit {
  private _alert = new Subject<string>();
  paymentIsCollapsed = true;
  planIsCollapsed = true;
  priceIsCollapsed = true;
  successMessage: string;
  errorMessage: string;
  subscription: Subscription;
  confirmationMessage;
  confirmationTitle;
  modalReference: NgbModalRef;

  billType: BillTypeSetup;
  billTypeId: string;
  communityId: string;
  billTypeName;

  planBillingCycle;
  planBillingCycles = Constants.BILLING_CYCLES;

  planUId;
  planName;
  planCode;
  planPrice;
  planDescription;
  selectedPlan;
  listedPlans: Array<any>;
  plan: BillTypePlan;
  plans: Array<BillTypePlan> = [];

  paymentStructureName;
  paymentStructure;
  paymentStructures = Constants.PAYMENT_STRUCTURES;

  paymentMethodName;
  paymentMethod;
  paymentMethods = Constants.PAYMENT_METHODS;

  paymentInstruction;
  paymentDetail: any;

  priceDetail: PriceDetail;
  isServicePriceSet = false;
  compulsory = false;
  activeRouteIndex;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private billTypeService: BillTypeService,
    private loaderService: LoaderService
  ) {
    this.route.params.subscribe(params => {
      this.activeRouteIndex = parseInt(params.index, 10);
      this.billTypeId = params.billTypeId;
      this.communityId = params.communityId;

      this.billTypeService.multicastAvailablePlans(
        this.communityId,
        this.billTypeId
      );
      this.billTypeService.bsBillTypePlans$.subscribe(
        result => (this.plans = result)
      );
    });

    this.planName = "";
    this.planPrice = "";
    this.planDescription = "";
    this.priceDetail = new PriceDetail();
    this.paymentStructure = "default";
    this.paymentMethod = "default";
  }

  ngOnInit(): void {
    this.billTypeService
      .getServicePrice(this.communityId, this.billTypeId)
      .subscribe(data => {
        if (data.length) {
          this.priceDetail = data[0];
          this.isServicePriceSet = true;
        }
      });

    this.billTypeService
      .getCommunityBillTypes(this.communityId)
      .subscribe(communityBillTypes => {
        this.billType = communityBillTypes.filter(
          communityBillType => communityBillType.id === this.billTypeId
        )[0];
        if (this.billType) {
          this.billTypeName = this.billType.name;

          this.compulsory = this.billType["compulsory"];

          this.paymentDetail = this.billType["payment"];

          if (this.paymentDetail) {
            this.paymentStructure = this.paymentDetail.structure;
            this.paymentMethod = this.paymentDetail.method;
            this.setPaymentStructureAndMethodName();
            this.paymentInstruction = this.paymentDetail.instruction;
          }
          this.loaderService.stopLoader();
        }
      });

    this.planBillingCycle = "default";
    this.listedPlans = [];
    this.selectedPlan = "";
    this.paymentInstruction = "";
  }

  ngAfterViewInit(): void {
    this.loaderService.startLoader();
  }

  togglePaymentCollapse(): void {
    this.paymentIsCollapsed = !this.paymentIsCollapsed;
    this.planIsCollapsed = true;
    this.priceIsCollapsed = true;
  }

  togglePlanCollapse(): void {
    this.planIsCollapsed = !this.planIsCollapsed;
    this.paymentIsCollapsed = true;
    this.priceIsCollapsed = true;
  }

  togglePriceCollapse(): void {
    this.priceIsCollapsed = !this.priceIsCollapsed;
    this.planIsCollapsed = true;
    this.paymentIsCollapsed = true;
  }

  savePaymentDetail(): void {
    this.paymentDetail = {
      paymentStructure: this.paymentStructure,
      paymentMethod: this.paymentMethod,
      paymentInstruction: this.paymentInstruction
    };
    this.setPaymentStructureAndMethodName();

    const status = this.billTypeService.updatePaymentDetails(
      this.communityId,
      this.billTypeId,
      this.paymentDetail,
      this.compulsory
    );

    if (status) {
      this.showAlert(true, "Payment details saved successfully.");
      this.plans = [];
      this.paymentIsCollapsed = true;
    } else {
      this.showAlert(false, "Opps, something went wrong, try again later.");
    }
  }

  addPlan(): void {
    for (const plan of this.listedPlans) {
      if (plan.name === this.planName || plan.code === this.planCode) {
        this.showAlert(false, "A plan with that name has been added already.");

        return;
      }
    }
    this.listedPlans.push({
      name: this.planName,
      code: this.planCode,
      price: this.planPrice,
      billingCycle: this.planBillingCycle,
      description: this.planDescription
    });
    this.planName = "";
    this.planCode = "";
    this.planPrice = "";
    this.planBillingCycle = "default";
    this.planDescription = "";
  }

  removePlan(index): void {
    this.listedPlans.splice(index, 1);
  }

  savePlan(): void {
    this.listedPlans.forEach(element => {
      this.plan = element;
      this.plans.push(this.plan);
    });

    try {
      const status = this.billTypeService.updatePlans(
        this.communityId,
        this.billType.id,
        this.plans
      );
      const length = this.listedPlans.length;
      const phrase = length === 1 ? "plan has" : "plans have";

      if (status) {
        this.planIsCollapsed = true;
        this.showAlert(true, "The " + phrase + " been successfully added.");
        this.listedPlans = [];
      } else {
        this.showAlert(
          false,
          "Opps, an error occurred while saving your " + phrase + "."
        );
      }
    } catch (e) {
      this.showAlert(
        false,
        "Opps, something unusual occurred, please try again later. "
      );
    }
  }

  confirmDelete(index, modal, planId: string): void {
    this.planUId = planId;
    this.selectedPlan = index;
    const message = "Are you sure you want to delete this plan?";
    this.confirm("Warning!", message, modal);
  }

  proceed(): void {
    this.close();
    if (this.selectedPlan !== "") {
      this.plans.splice(this.selectedPlan, 1);
      const status = this.billTypeService.removePlan(
        this.communityId,
        this.billTypeId,
        this.planUId
      );
      if (status) {
        this.showAlert(true, "Plan deleted successfully.");
      } else {
      }

      this.selectedPlan = "";
    }
  }

  editPlan(index, modal, planUId: string): void {
    this.selectedPlan = index;
    this.planUId = planUId;
    this.planName = this.plans[index].name;
    this.planCode = this.plans[index].code;
    this.planPrice = this.plans[index].price;
    this.planBillingCycle = this.plans[index].billingCycle;
    this.planDescription = this.plans[index].description;
    this.openModal(modal, "lg");
  }

  updatePlan(): void {
    const plan = new Plan();
    plan.id = this.planUId;
    plan.name = this.planName;
    plan.code = this.planCode;
    plan.price = this.planPrice;
    plan.billingCycle = this.planBillingCycle;
    plan.description = this.planDescription;
    this.close();
    if (
      this.billTypeService.updatePlan(plan, this.communityId, this.billTypeId)
    ) {
      this.showAlert(true, "Plan updated successfully.");
    } else {
      this.showAlert(
        false,
        "Opps, something went wrong while updating your plan, please try again."
      );
    }

    this.selectedPlan = "";
    this.close();
  }

  savePrice(): void {
    const status = this.billTypeService.updatePrice(
      this.communityId,
      this.billTypeId,
      this.priceDetail
    );

    if (status) {
      this.isServicePriceSet = Object.keys(this.priceDetail).length > 0;
      this.showAlert(true, "Price details saved successfully.");
    } else {
    }

    this.priceIsCollapsed = true;
  }

  getBillTypeByID(id): any {
    return this.billTypeService
      .getFormattedBillTypes()
      .filter(billType => billType.id === id)[0];
  }

  setPaymentStructureAndMethodName(): void {
    this.paymentStructureName = this.billTypeService.getObjectNameWithId(
      this.paymentStructure,
      this.paymentStructures
    );
    this.paymentMethodName = this.billTypeService.getObjectNameWithId(
      this.paymentMethod,
      this.paymentMethods
    );
  }

  showAlert(msgType, msg): void {
    this.subscription = this._alert.subscribe(
      message =>
        msgType
          ? (this.successMessage = message)
          : (this.errorMessage = message)
    );
    debounceTime
      .call(this._alert, 3000)
      .subscribe(
        () =>
          msgType
            ? (this.successMessage = undefined)
            : (this.errorMessage = undefined)
      );
    this._alert.next(msg);
    this.subscription.unsubscribe();
  }

  confirm(title, message, modal): void {
    this.confirmationTitle = title;
    this.confirmationMessage = message;
    this.openModal(modal, "sm");
  }

  openModal(modal, modalSize): void {
    this.modalReference = this.modalService.open(modal, { size: modalSize });
  }

  close(): void {
    this.modalReference.close();
  }

  isNumber(event: any): boolean {
    const charCode = event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }

    return true;
  }
}



// WEBPACK FOOTER //
// ./src/app/bill/set-up-bill-type/set-up-bill-type.component.ts